import React, { Dispatch, ForwardedRef, forwardRef, SetStateAction, useImperativeHandle } from "react";
import SaveProfile from "./edit-quiz-modals/SaveProfileWrapper";
import styles from "./editQuiz.module.css";
import { api as defaultApi, VerityApi, VerityApiContext } from "../api/fetch";
import { AssessmentInfo } from "@verity/api-client/types";
import { StepConfig } from "../core";
import {
	ProctoringAssessmentConfigurationV2,
	ProctoringOption,
	ProctoringOptionProfileV2,
} from "@verity/proctoring-option/types";
import {
	EditQuizStep,
	isProctoringOptionCategory,
	RenderableProctoringKey,
} from "./proctoring-options/option-text/ProctoringOptionText";
import { VerityLogger } from "@yujacorp/verity-logger";
import i18nPackage from "../i18n/i18n";
import { I18nextProvider } from "react-i18next";
import type { i18n } from "i18next";
import AccommodationStep from "./edit-quiz-steps/AccommodationStep";
import ConfirmationPage from "./edit-quiz-steps/ConfirmationStep";
import ConfigurationStep from "./edit-quiz-steps/ConfigurationStep";
import LiveSessionsStep from "./edit-quiz-steps/LiveSessionStep";
import MethodStep from "./edit-quiz-steps/MethodStep";
import PlatformStep from "./edit-quiz-steps/PlatformStep";
import ThirdPartyStep from "./edit-quiz-steps/ThirdPartyStep";
import DisclaimerModal from "./edit-quiz-modals/disclaimerModal";
import NoConfigurationsEnabledModal from "./edit-quiz-modals/noConfirmationEnabledModal";
import useEditQuizNavigation from "./hooks/useEditQuizNavigation";
import useEditQuizSubmit from "./hooks/useEditQuizSubmit";
import useEditQuizInit from "./hooks/useEditQuizInit";
import { EditQuizEntryPoint, EditQuizType, ExemptedStudent } from "../types/common";
import UnsavedChangesModal from "./edit-quiz-modals/UnsavedChangesModal";
import { InitialProctoringState, UnsavedAssessmentInfo } from "./editQuizModalWrapper";
import useEditQuizState from "./hooks/state/useEditQuizState";
import ProfileStep from "./edit-quiz-steps/ProfileStep";
import useEditQuizProfile from "./hooks/useEditQuizProfile";
import EditQuizStepper from "./components/EditQuizStepper";
import { EditQuizAssessmentUUids, LiveSessionWithInvitations, PendingLiveSession } from "./live/liveHelper";

export type EditQuizButtonState = {
	enabled?: boolean;
	hide?: boolean;
	tooltip?: React.ReactNode;
	text?: React.ReactNode;
};

export type EditQuizPageProps = {
	quizName?: string;
	courseName?: string;
	url?: string;
	api?: VerityApi;
	quizInfo: UnsavedAssessmentInfo;
	initialProctoringAssessmentConfiguration?: Omit<
		Partial<ProctoringAssessmentConfigurationV2>,
		"proctoringOptionProfile"
	>;
	proctoringAssessmentConfiguration?: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;
	setProctoringAssessmentConfiguration: (
		options: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">,
	) => void;
	initialProctoringOption?: Partial<ProctoringOption>;
	proctoringOption?: Partial<ProctoringOption>;

	setProctoringOption: (options: Partial<ProctoringOption>) => void;
	currentProfileId?: number;
	setCurrentProfileId: (id: number | undefined) => void;
	existingLiveSessions: Record<string, LiveSessionWithInvitations>;
	setExistingLiveSessions: React.Dispatch<React.SetStateAction<Record<string, LiveSessionWithInvitations>>>;
	pendingLiveSessions: Record<string, PendingLiveSession>;
	setPendingLiveSessions: React.Dispatch<React.SetStateAction<Record<string, PendingLiveSession>>>;
	pendingLiveSessionIds: string[];
	setPendingLiveSessionIds: React.Dispatch<React.SetStateAction<string[]>>;
	currFakeLiveSessionId: React.MutableRefObject<number>;
	assessmentUuids: React.MutableRefObject<EditQuizAssessmentUUids | undefined>;
	enforcedProfilesOnly: boolean;

	onBackEnabledChange?: (changes: EditQuizButtonState) => void;
	onNextEnabledChange?: (changes: EditQuizButtonState) => void;
	/** Called when the proctoring options can or cannot be saved */
	onSaveEnabledChange?: (changes: EditQuizButtonState) => void;
	onSaveProfileChange?: (changes: EditQuizButtonState) => void;
	setIsFirstPage?: (value: boolean) => void;
	setIsFinalPage?: (value: boolean) => void;
	forceStartOnFirstPage?: boolean;

	/** Called right before the options are being saved */
	onStartSaveOptions?: (confirmedOptions?: InitialProctoringState) => void;
	/** Called when options have finished saving (`success = true`)
	 * or failed to save (`success = false`). A `message` may be set if `success` is `false`. */
	onSaveOptions: (
		success: boolean,
		messages?: string[],
		options?: Parameters<VerityApi["saveProctoringAssessmentConfiguration"]>[0],
	) => void;
	uploadedExemptedStudentsMap: Map<number, ExemptedStudent>;
	setUploadedExemptedStudentsMap: React.Dispatch<React.SetStateAction<Map<number, ExemptedStudent>>>;
	uploadedProfilesMap: Map<number, ProctoringOptionProfileV2>;
	setUploadedProfilesMap: React.Dispatch<React.SetStateAction<Map<number, ProctoringOptionProfileV2>>>;
	logger?: VerityLogger;

	onClose?: (confirmedOption?: Partial<ProctoringOption>) => void;

	defaultProctoringOption?: ProctoringOption | null;

	/* Wraps the child component (stepper) in a predefined component */
	step?: number;
	setStep?: Dispatch<SetStateAction<number>>;

	/**@default "lms" */
	quizType?: EditQuizType;
	/**@default "lms-modal" */
	entryPoint?: EditQuizEntryPoint;
};

export type EditQuizRef = {
	saveButtonHandler: (password?: string, additionalAssessmentInfo?: Partial<AssessmentInfo>) => Promise<boolean>;
	closeButtonHandler: () => Promise<boolean>;
	saveAsProfileButtonHandler: () => void;
	nextButtonHandler: () => void;
	backButtonHandler: () => void;
	goToPage: (pageName: EditQuizStep, proctoringOptionToFocus?: RenderableProctoringKey) => void;
	getStepKeyByIndex: (index: number) => EditQuizStep;
	getStepConfig: () => StepConfig<EditQuizStep>[];

	// Exposed for verity assessment workflow because the confirmation step is rendered outside edit quiz
	getActiveProfile: () => ProctoringOptionProfileV2 | undefined;
	getNumExempted: () => number;
};

export const EditQuizPage = forwardRef(function EditQuizPage(
	{
		api = defaultApi,
		forceStartOnFirstPage = false,
		quizType = "lms",
		entryPoint = "lms-modal",
		...props
	}: EditQuizPageProps,
	ref: ForwardedRef<EditQuizRef>,
) {
	// Moved init logic to respective hooks for organization
	const {
		live: liveConfig,
		nav: navConfig,
		profile: profileConfig,
		submit: submitConfig,
		setting: settingConfig,
		student: studentConfig,
		theme: themeConfig,
	} = useEditQuizState({ ...props, api, forceStartOnFirstPage, quizType, entryPoint });

	const {
		enforcedProfilesOnly,
		onSaveOptions,
		quizInfo,
		setCurrentProfileId,
		setProctoringAssessmentConfiguration,
		setProctoringOption,
		initialProctoringAssessmentConfiguration,
		logger,
		onBackEnabledChange,
		onNextEnabledChange,
		onSaveEnabledChange,
		onStartSaveOptions,
		proctoringAssessmentConfiguration,
		proctoringOption,
		setIsFinalPage,
		url,
		onSaveProfileChange,
		quizName,
		onClose,
		currentProfileId,
		setUploadedProfilesMap,
		setIsFirstPage,
	} = props;

	const navHook = useEditQuizNavigation({
		enforcedProfilesOnly,
		live: liveConfig,
		nav: navConfig,
		profile: profileConfig,
		setProctoringAssessmentConfiguration,
		setting: settingConfig,
		onBackEnabledChange,
		onNextEnabledChange,
		proctoringAssessmentConfiguration,
		setIsFinalPage,
		setIsFirstPage,
		quizType,
		entryPoint,
	});
	const {
		curPageName,
		handleBackButton,
		handleGoToStep,
		handleNextButton,
		handleStepChange,
		getStepKeyByIndex,
		completedStepIndexes,
		stepConfig,
		handleConfirmLiveWarning,
	} = navHook;

	useEditQuizInit({
		enforcedProfilesOnly,
		live: liveConfig,
		profile: profileConfig,
		setProctoringAssessmentConfiguration,
		setting: settingConfig,
		student: studentConfig,
		theme: themeConfig,
		proctoringAssessmentConfiguration,
		url,
	});

	const { saveButtonHandler, closeButtonHandler } = useEditQuizSubmit({
		api,
		enforcedProfilesOnly,
		live: liveConfig,
		onSaveOptions,
		profile: profileConfig,
		quizInfo,
		submit: submitConfig,
		setCurrentProfileId,
		setting: settingConfig,
		student: studentConfig,
		theme: themeConfig,
		logger,
		onSaveEnabledChange,
		onStartSaveOptions,
		proctoringAssessmentConfiguration,
		proctoringOption,
		onClose,
		quizType,
	});

	const { handleDeleteProfile, handleSaveProfile, handleSelectProfile, handleUpdateProfile } = useEditQuizProfile({
		profile: profileConfig,
		onSaveProfileChange,
		setting: settingConfig,
		quizInfo,
		setProctoringOption,
		currentProfileId,
		proctoringAssessmentConfiguration,
		proctoringOption,
		setUploadedProfilesMap,
	});

	useImperativeHandle(
		ref,
		() => ({
			saveButtonHandler,
			closeButtonHandler,
			saveAsProfileButtonHandler: profileConfig.action.saveAsProfileButtonHandler,
			nextButtonHandler: handleNextButton,
			backButtonHandler: handleBackButton,
			goToPage: handleGoToStep,
			getStepConfig: () => stepConfig,
			getStepKeyByIndex,
			getActiveProfile: () => profileConfig.state.activeProfile,
			getNumExempted: () => studentConfig.state.numExempted,
		}),
		[
			saveButtonHandler,
			closeButtonHandler,
			profileConfig.action.saveAsProfileButtonHandler,
			profileConfig.state.activeProfile,
			studentConfig.state.numExempted,
			handleNextButton,
			handleBackButton,
			handleGoToStep,
			getStepKeyByIndex,
			stepConfig,
		],
	);

	// Destructuring common props
	const { verityInstituteSettings, optionRelatedInstituteSettings, loading, enabledProctoringOptions, proctoringData } =
		settingConfig.state;

	if (!proctoringAssessmentConfiguration || !proctoringOption || !verityInstituteSettings) return null;

	// Stepper is hidden for in page standalone because in page standalone handles its own navigation -> will be refactored to not do that.
	const hideStepper = quizType === "standalone" && entryPoint === "lti-page";

	return (
		<VerityApiContext.Provider value={api}>
			<I18nextProvider i18n={i18nPackage as i18n}>
				<form
					aria-label={i18nPackage.t("editQuizPage.formAriaLabel")}
					id="yuja-options-form"
					name="yuja-options-form"
					className={styles.editQuizContainer}
				>
					{!hideStepper && (
						<div className={styles.stepperContainer}>
							<EditQuizStepper
								activeStep={navConfig.state.curPageValue}
								steps={stepConfig}
								onStepChange={handleStepChange}
								completedSteps={completedStepIndexes}
								loading={loading}
							/>
						</div>
					)}

					<div tabIndex={-1} ref={navConfig.state.modalBodyRef} className={styles.modalBody}>
						{proctoringAssessmentConfiguration.thirdPartyAssessmentOption && curPageName === "thirdParty" && (
							<ThirdPartyStep
								isFirstTime={!initialProctoringAssessmentConfiguration?.enabled}
								thirdPartyPassword={proctoringAssessmentConfiguration.thirdPartyAssessmentOption?.password}
								setThirdPartyPassword={(password) =>
									setProctoringAssessmentConfiguration({
										...proctoringAssessmentConfiguration,
										thirdPartyAssessmentOption: {
											...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
											password: password,
										},
									})
								}
								selectedPlatformId={proctoringAssessmentConfiguration.thirdPartyAssessmentOption.thirdPartyPlatformId}
								setSelectedPlatformId={(platformId) =>
									setProctoringAssessmentConfiguration({
										...proctoringAssessmentConfiguration,
										thirdPartyAssessmentOption: {
											...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
											thirdPartyPlatformId: platformId,
										},
									})
								}
								platforms={proctoringData.thirdPartyPlatforms}
								proctoringOption={proctoringOption}
								setProctoringOption={setProctoringOption}
								loading={loading}
							/>
						)}

						{curPageName === "platform" && (
							<PlatformStep
								forceChromeOsManagedGuestSession={proctoringAssessmentConfiguration.forceChromeOsManagedGuestSession}
								loading={loading}
								enforcedProfilesOnly={enforcedProfilesOnly}
								proctoringOption={proctoringOption}
								setProctoringOption={setProctoringOption}
								verityInstituteSettings={verityInstituteSettings}
								optionRelatedInstituteSettings={optionRelatedInstituteSettings}
							/>
						)}
						{curPageName === "profile" && (
							<ProfileStep
								profiles={profileConfig.state.profiles}
								proctoringData={proctoringData}
								activeProfile={profileConfig.state.activeProfile}
								onProfileSelect={handleSelectProfile}
								enforcedProfilesOnly={enforcedProfilesOnly}
								verityInstituteSettings={verityInstituteSettings}
								optionRelatedInstituteSettings={optionRelatedInstituteSettings}
								loading={loading}
								showPresetProfile={profileConfig.state.showPresetGridUI}
								canDeleteProfile={profileConfig.action.isDeleteProfileAllowed}
								onDelete={handleDeleteProfile}
								isCustomConfig={profileConfig.state.isCustomConfig}
							/>
						)}

						{isProctoringOptionCategory(curPageName) && (
							<ConfigurationStep
								loading={loading}
								data={proctoringData}
								currentCategory={curPageName}
								enabledProctoringOptions={enabledProctoringOptions}
								enforcedProfilesOnly={enforcedProfilesOnly}
								proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
								proctoringOption={proctoringOption}
								setProctoringOption={setProctoringOption}
								verityInstituteSettings={verityInstituteSettings}
								optionRelatedInstituteSettings={optionRelatedInstituteSettings}
								proctoringOptionToFocus={navConfig.state.proctoringOptionToFocus}
								onFocusProctoringOption={navConfig.action.resetProctoringOptionToFocus}
								quizType={quizType}
							/>
						)}

						{curPageName === "method" && (
							<MethodStep
								enabledProctoringOptions={enabledProctoringOptions}
								updateOptions={setProctoringAssessmentConfiguration}
								hasUpcomingSessions={() => {
									const currTime = new Date();
									return Object.values(liveConfig.state.pendingLiveSessions).some(
										({ startTime }) => startTime > currTime,
									);
								}}
								unmarkStepAsCompleted={navConfig.action.unmarkStepAsCompleted}
								options={proctoringAssessmentConfiguration}
								verityInstituteSettings={verityInstituteSettings}
								loading={loading}
							/>
						)}

						{curPageName === "liveSessions" && (
							<LiveSessionsStep
								existingLiveSessions={liveConfig.state.existingLiveSessions}
								pendingLiveSessionIds={liveConfig.state.pendingLiveSessionIds}
								pendingLiveSessions={liveConfig.state.pendingLiveSessions}
								syncStartedSessions={liveConfig.action.syncStartedSessions}
								availableProctorGroups={liveConfig.state.availableProctorGroups}
								availableProctors={liveConfig.state.availableProctors}
								liveSessionsNextConfirmationMessage={liveConfig.state.liveSessionsNextConfirmationMessage}
								cancelNextConfirmationDialog={liveConfig.action.resetConfirmationMessage}
								confirmNextConfirmationDialog={handleConfirmLiveWarning}
								allowDemoSessions={verityInstituteSettings.allowDemoMode}
								addPendingSession={liveConfig.action.addPendingSession}
								removePendingSession={liveConfig.action.removePendingSession}
								updatePendingSession={liveConfig.action.updatePendingSession}
							/>
						)}

						{curPageName === "accommodations" && (
							<AccommodationStep
								exemptionInfo={studentConfig.state.exemptionInfo}
								onRosterChange={studentConfig.action.updateRoster}
								profilesMap={profileConfig.state.profileMap}
								enforcedProfilesOnly={enforcedProfilesOnly}
								optionRelatedInstituteSettings={optionRelatedInstituteSettings}
								proctoringData={proctoringData}
								verityInstituteSettings={verityInstituteSettings}
								canDeleteProfile={profileConfig.action.isDeleteProfileAllowed}
								onDelete={handleDeleteProfile}
								loading={loading}
							/>
						)}

						{curPageName === "confirmation" && (
							<ConfirmationPage
								quizName={quizName}
								exemptionCount={studentConfig.state.exemptionInfo.exemptRoster.size}
								pendingLiveSessions={liveConfig.state.pendingLiveSessionIds.map(
									(id) => liveConfig.state.pendingLiveSessions[id],
								)}
								loading={loading}
								handleGoToStep={handleGoToStep}
								quizType={quizType}
								data={proctoringData}
								enforcedProfilesOnly={enforcedProfilesOnly}
								proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
								proctoringOption={proctoringOption}
								verityInstituteSettings={verityInstituteSettings}
								optionRelatedInstituteSettings={optionRelatedInstituteSettings}
								activeProfile={profileConfig.state.activeProfile}
							/>
						)}

						{quizType !== "standalone" &&
							!proctoringAssessmentConfiguration?.thirdPartyAssessmentOption &&
							curPageName === "confirmation" &&
							submitConfig.state.disclaimerModalOpen && (
								<DisclaimerModal
									verifyingTheme={themeConfig.state.verifyingTheme}
									onClose={submitConfig.action.closeDisclaimerModal}
									onSubmit={submitConfig.action.submitDisclaimerModal}
								/>
							)}

						{profileConfig.state.saveProfileModalShowing && (
							<SaveProfile
								activeProfile={profileConfig.state.activeProfile}
								onClose={() => profileConfig.action.hideSaveProfileModal()}
								hasInstituteProfilePermissions={profileConfig.state.hasInstituteProfilePermissions}
								onSaveNewProfile={handleSaveProfile}
								onUpdateProfile={handleUpdateProfile}
								canPublishToCourse={Boolean(quizInfo.courseId)}
								isThirdPartyEnabled={Boolean(proctoringAssessmentConfiguration.thirdPartyAssessmentOption)}
							/>
						)}

						{submitConfig.state.unsavedChangesModalOpen && (
							<UnsavedChangesModal
								onClose={submitConfig.action.closeUnsavedChangesModal}
								onConfirm={submitConfig.action.confirmUnsavedChangesModal}
							/>
						)}

						{submitConfig.state.noConfigurationsEnabledModalOpen && (
							<NoConfigurationsEnabledModal
								onClose={submitConfig.action.closeNoConfigModal}
								onConfirm={submitConfig.action.confirmNoConfigModal}
							/>
						)}
					</div>
				</form>
			</I18nextProvider>
		</VerityApiContext.Provider>
	);
});
